<template>
  <div
    class="base-group"
    :class="{ fill: fill }"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
/**
 * Visual grouping component for buttons and inputs
 *
 * Creates seamlessly connected groups by overlapping borders and removing
 * corner radii at connection points. Useful for button toolbars, search
 * bars, and segmented controls.
 *
 * @example Button group
 * ```vue
 * <BaseGroup>
 *   <BaseButton variant="outline">Left</BaseButton>
 *   <BaseButton variant="outline">Center</BaseButton>
 *   <BaseButton variant="outline">Right</BaseButton>
 * </BaseGroup>
 * ```
 *
 * @example Input with button
 * ```vue
 * <BaseGroup>
 *   <BaseInput v-model="search" placeholder="Search..." />
 *   <BaseButton icon="magnifying-glass" square />
 * </BaseGroup>
 * ```
 *
 * @example Full-width group with equal items
 * ```vue
 * <BaseGroup fill>
 *   <BaseButton variant="outline">Option A</BaseButton>
 *   <BaseButton variant="outline">Option B</BaseButton>
 *   <BaseButton variant="outline">Option C</BaseButton>
 * </BaseGroup>
 * ```
 */
export interface BaseGroupProps {
  /**
   * Makes group full-width with all children growing equally
   * Each child receives flex-grow: 1 to share available space
   * @default false
   */
  fill?: boolean;
}

withDefaults(defineProps<BaseGroupProps>(), {
  fill: false,
});
</script>

<style>
.base-group {
  align-content: center;
  display: flex;
  gap: 0;

  &.fill {
    width: 100%;
    > * {
      flex-grow: 1;
    }
  }

  > *:not(:first-child) {
    margin-left: calc(-1 * var(--size-border-thin));
  }

  > *:first-child {
    --input-border-radius-right: 0;
    --button-border-radius-right: 0;
  }

  > *:last-child {
    --input-border-radius-left: 0;
    --button-border-radius-left: 0;
  }

  > *:not(:first-child):not(:last-child) {
    --input-border-radius-right: 0;
    --button-border-radius-right: 0;
    --input-border-radius-left: 0;
    --button-border-radius-left: 0;
  }
}
</style>
